import { omit } from "lodash-es";
import {
    DEFAULT_CONTENT,
    BYLINE,
    DEFAULT_AUTHOR,
} from "../../../actions/Detail/constants";
import { getAdExclusions } from "../../advertising/library";
import { unixToZonedBylineDate } from "../../dateTime/library";
import { TYPE_POLOPOLY_IMAGE } from "../../storyBodyMarkupBuilder/constants";

export const parseAuthorList = (json) => {
    const authorList = [];

    // Author List exist
    if (Array.isArray(json.authors) && json.authors.length) {
        json.authors.forEach((props) => {
            authorList.push({
                ...DEFAULT_AUTHOR,
                name: props.name,
                sourceId: props.id,
                title: props.title,
                biography: props.biography,
                url: props.url,
                photoURL: props.photo?.aspects?.ratio1x1?.url || "",
                links: props.links,
            });
        });
        return authorList;
    }

    // Json is an Author content type coming from GraphQL
    if (json.photo) {
        authorList.push({
            ...DEFAULT_AUTHOR,
            name: json.name,
            sourceId: json.sourceId,
            title: json.title,
            biography: json.biography,
            url: json.url,
            photoURL: json.photo?.aspects?.ratio1x1?.url || "",
            links: json.links,
        });
        return authorList;
    }

    return [];
};

export const getSingleAuthor = (json) => {
    return parseAuthorList(json)[0];
};

/**
 * Create a byline for detail.content
 * @param {string} name
 * @returns {string}
 */
const createByLine = (name) => {
    if (typeof name === "string") {
        if (name === "Reuters") {
            return BYLINE.REUTERS;
        }
        return name;
    }
    return "";
};

const normalizeGrapeshot = (str = "") =>
    str.trim().replace(/[^a-zA-Z0-9-_]+/g, "-");

export const getGrapeshotCategories = (channels) => {
    return Array.from(
        channels.reduce(
            (accum, channel) => accum.add(normalizeGrapeshot(channel.name)),
            new Set()
        )
    );
};

export const getGrapeshotMatchterms = (channels) => {
    return Array.from(
        channels.reduce(
            (accum, channel) =>
                channel.matchterms.reduce(
                    (accum, term) => accum.add(normalizeGrapeshot(term)),
                    accum
                ),
            new Set()
        )
    );
};

/**
 * @description Trims name and title properties from jsob argument and returns the values concatinated with a ',' delimiter.
 * @param {Object} json
 * @param {string} json.name
 * @param {string} json.title
 * @returns {string}
 */
export const createAuthorbioHeadline = (json) => {
    if (json.type !== "authorbio" || !json.name) {
        return "";
    }
    // Super safe check!, can't create an author Object on the cms with at least a name filled out
    return json.title !== ""
        ? `${json.name.trim()} - ${json.title.trim()}`
        : json.name.trim();
};

/**
 * @description Checks if story contains images.
 * @param {Object} json
 * @param {array} json.body
 * @returns {boolean}
 */
const hasStoryImages = (json) => {
    if (json?.leadMedia?.__typename === "Image") {
        return true;
    }

    let hasImage = false;
    if (json?.body?.length) {
        hasImage = json.body.some((content) => {
            return (
                Array.isArray(content?.content) &&
                content.content.some(
                    (item) => item.type === TYPE_POLOPOLY_IMAGE
                )
            );
        });
    }
    return hasImage;
};

/**
 * @param {Object} json
 * @param {Object} json.advertising
 * @param {string[]} json.advertising.exclusions
 * @param {Object} json.attribution
 * @param {string} json.authorDisplay
 * @param {array} json.authors
 * @param {array} json.body
 * @param {string} json.byline
 * @param {array} json.clarifications
 * @param {array} json.concepts
 * @param {array} json.corrections
 * @param {array} json.deck
 * @param {array} json.departments
 * @param {string} json.description
 * @param {boolean} json.displaycomments
 * @param {Object} json.episode
 * @param {array} json.episode.episode.segments
 * @param {array} json.externalLinks
 * @param {string} json.flag
 * @param {boolean} json.hasaudio
 * @param {boolean} json.hasvideo
 * @param {boolean} json.hasgallery
 * @param {Object} json.highlights
 * @param {array} json.intlinks
 * @param {string} json.language
 * @param {number} json.mediaid
 * @param {string} json.newsletters
 * @param {number} json.publishedAt
 * @param {array} json.segments
 * @param {string} json.source
 * @param {Object} json.sponsor
 * @param {array} json.tags
 * @param {Object} json.tracking
 * @param {string} json.type
 * @param {number} json.updatedAt
 * @param {string} json.url
 * @param {number} json.wordcount
 * @returns {Object}
 */
export const createContent = (json) => {
    const content = {
        ...DEFAULT_CONTENT,
        attribution: json.attribution || {},
        authorDisplay: json.authorDisplay,
        authorList: parseAuthorList(json),
        body: json.body,
        byline: createByLine(json.byline),
        clarifications: json.clarifications || [],
        corrections: json.corrections || [],
        deck: json.deck || "",
        departments: json.departments || [],
        description: json.description || "",
        displayComments: json.displaycomments || false,
        externalLinks: json.externalLinks || [],
        flag: json.flag || "",
        headline: json.headline || "",
        highlights: json.highlights || {},
        id: json.id,
        intlinks: json.intlinks || [],
        newsletter: json.newsletter || false,
        publishedAt: parseInt(json.publishedAt) || 0,
        publishedAtVerbal: unixToZonedBylineDate(json.publishedAt) || "",
        source: json.source || "",
        type: json.type,
        updatedAt: parseInt(json.updatedAt) || 0,
        url: json.url || "",
        language: json.language || "",
    };
    // some types like media don't have an advertising Object

    if (json.advertising) {
        if (json.advertising.exclusions) {
            content.adExclusions = getAdExclusions(json.advertising.exclusions);
        }
        if (json.advertising.categorization) {
            const channels = json.advertising.categorization.channels;
            if (channels && Object.entries(channels).length > 0) {
                content.gs_categories = getGrapeshotCategories(channels);
                content.gs_keywords = getGrapeshotMatchterms(channels);
            }
        }
    }
    if (json.mediaid) {
        content.mediaid = json.mediaid;
    }
    if (json.type === "segment") {
        content.episode = omit(json.episode, ["episode"]) || {};
        content.segments = json.episode?.episode?.segments || [];
    }
    if (json.type === "episode") {
        content.segments = json.segments || [];
    }

    if (json.type === "sponsoredcontent") {
        content.sponsorBio = json.sponsor || {};
    }

    content.tags = json.tags || [];
    content.concepts = json.concepts || [];
    content.keywords = { tags: content.tags, concepts: content.concepts };
    content.subject = content.concepts
        .filter(({ type }) => type === "subject")
        .map((item) => {
            return { type: item.type, name: item.path };
        });

    if (/\preview\//.test(json.url)) {
        content.isPreview = true;
    }

    if (Number.isInteger(json.wordcount)) {
        content.wordcount = json.wordcount || 0;
    }

    const embedTypes = [];

    if (json.hasaudio) {
        embedTypes.push("audio");
    }
    if (json.hasvideo) {
        embedTypes.push("video");
    }
    if (hasStoryImages(json)) {
        embedTypes.push("photo");
    }
    if (json.hasgallery) {
        embedTypes.push("photogallery");
    }
    content.embedtypes = embedTypes;

    if (json.tracking) {
        content.subsection1 = json.tracking.subSection1 || "";
        content.subsection2 = json.tracking.subSection2 || "";
        content.subsection3 = json.tracking.subSection3 || "";
        content.subsection4 = json.tracking.subSection4 || "";
        content.contentarea = json.tracking.contentArea || "";
    }

    return content;
};
